/**
 * UxInsightsCard — iter.255 · Fase 12 UX Refactor (métricas cross-device)
 * ----------------------------------------------------------------------
 * Card en AdminDashboard mostrando distribución agregada de:
 *  - Modo Simple vs Avanzado
 *  - Tema (light/dark/system)
 *  - Sidebar colapsado (true/false)
 *  - Pantalla de inicio (default_landing)
 *
 * Datos: GET /api/admin/ux/insights (ya existente, ampliado en Fase 12).
 */
import { useEffect, useState } from "react";
import { PieChart, Palette, LayoutDashboard, Sidebar as SidebarIcon } from "lucide-react";
import { adminFetch } from "../../pages/admin/adminUtils";

function DistributionRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1" data-testid={`ux-dist-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-300 font-medium">{label}</span>
        <span className="text-muted-foreground">{count} · {pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function DistributionBlock({ icon: Icon, title, rows, palette, keyProp = "label", countProp = "users" }) {
  const total = rows.reduce((acc, r) => acc + (r[countProp] || 0), 0);
  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-slate-800 text-slate-300 flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="text-slate-200 text-sm font-semibold">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">Sin datos todavía.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, i) => (
            <DistributionRow
              key={String(row[keyProp]) + i}
              label={String(row[keyProp] ?? "—")}
              count={row[countProp] || 0}
              total={total}
              color={palette[i % palette.length]}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function UxInsightsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminFetch("/api/admin/ux/insights?days=30");
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-5"
      data-testid="ux-insights-card"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-slate-200 font-semibold">UX Insights · adopción cross-device</h2>
          <p className="text-muted-foreground text-xs mt-0.5">
            Distribución agregada de preferencias UI (últimos 30 días)
          </p>
        </div>
        <PieChart className="w-4 h-4 text-muted-foreground" />
      </div>

      {loading && (
        <div className="h-32 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && !loading && (
        <p className="text-red-400 text-sm">No se pudieron cargar métricas: {error}</p>
      )}

      {!loading && !error && data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DistributionBlock
            icon={LayoutDashboard}
            title="Modo UI"
            rows={data.current_distribution || []}
            palette={["#F97316", "#60A5FA"]}
            keyProp="ui_mode"
          />
          <DistributionBlock
            icon={Palette}
            title="Tema"
            rows={data.theme_distribution || []}
            palette={["#FBBF24", "#8B5CF6", "#22C55E"]}
            keyProp="theme"
          />
          <DistributionBlock
            icon={SidebarIcon}
            title="Menú lateral"
            rows={(data.sidebar_distribution || []).map(r => ({
              ...r,
              label: r.collapsed ? "Colapsado" : "Expandido",
            }))}
            palette={["#94A3B8", "#22C55E"]}
            keyProp="label"
          />
          <DistributionBlock
            icon={LayoutDashboard}
            title="Pantalla de inicio"
            rows={data.landing_distribution || []}
            palette={["#F97316", "#60A5FA", "#8B5CF6", "#22C55E", "#F43F5E"]}
            keyProp="default_landing"
          />
        </div>
      )}
    </div>
  );
}
